import { router } from '@inertiajs/react'
import { useEffect, useState, useCallback, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import Layout from '@/components/layout/Layout'
import ChatPanel from '@/components/demos/ChatPanel'
import ChatInput from '@/components/demos/ChatInput'
import PreviewPanel from '@/components/demos/PreviewPanel'
import DemoBriefPanel from '@/components/demos/DemoBriefPanel'
import { ArrowLeft, Video, Copy, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface AskUserQuestion {
  question: string
  input_type: 'text' | 'buttons' | 'dropdown'
  options?: string[]
}

interface Message {
  id: string
  role: string
  content: string
  created_at: string
  message_type?: string
  metadata?: AskUserQuestion
  answered?: string
}

interface SessionData {
  id: string
  name: string
  slug: string
  status: string
  mode: string
  draft_html: string | null
  clip_url: string | null
  recorded_at: string | null
  record_error: string | null
  processing: boolean
  brief_functionality: string | null
  brief_design_direction: string | null
  brief_reference_demo_ids: string[]
  duration_seconds: number
}

interface ReferenceCandidate {
  id: string
  name: string
}

interface Props {
  session: SessionData
  messages: Message[]
  reference_candidates: ReferenceCandidate[]
}

interface ActivityEvent {
  id: string
  tool: string
  summary: string
  detail?: Record<string, unknown>
  turn?: number
  timestamp: number
}

interface CableData {
  type: 'text' | 'complete' | 'error' | 'recorded' | 'record_error' | 'ask_user' | 'activity' | 'draft_update' | 'init' | 'thinking' | 'tool_result'
  content?: string
  draft_html?: string
  message?: string
  clip_url?: string
  question?: AskUserQuestion
  tool?: string
  summary?: string
  detail?: Record<string, unknown>
  turn?: number
  model?: string
  tools?: string[]
  cwd?: string
  tool_use_id?: string
  is_error?: boolean
  preview?: string
  stats?: {
    duration_s: number
    num_turns: number
    cost_usd?: number
    stop_reason?: string
  }
}

let cable: ReturnType<typeof createConsumer> | null = null
function getCable() {
  if (!cable) cable = createConsumer()
  return cable
}

export default function DemoShow({ session, messages: initialMessages, reference_candidates: referenceCandidates }: Props) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [draftHtml, setDraftHtml] = useState<string | null>(session.draft_html)
  const [status, setStatus] = useState(session.status)
  const [clipUrl, setClipUrl] = useState(session.clip_url)
  const [streaming, setStreaming] = useState(session.processing || false)
  const [recording, setRecording] = useState(session.status === 'recording')
  const [recordError, setRecordError] = useState<string | null>(session.record_error)
  const [activity, setActivity] = useState<ActivityEvent[]>([])
  const [agentInfo, setAgentInfo] = useState<{ model?: string; tools?: string[] }>({})
  const [stats, setStats] = useState<{ duration_s: number; num_turns: number; cost_usd?: number } | null>(null)
  const streamingContentRef = useRef('')

  useEffect(() => {
    const subscription = getCable().subscriptions.create(
      { channel: 'DemoChannel', session_id: session.id },
      {
        received(data: CableData) {
          if (data.type === 'text') {
            streamingContentRef.current += data.content || ''
            setMessages(prev => {
              const last = prev[prev.length - 1]
              if (last && last.role === 'assistant' && last.id === 'streaming') {
                return [...prev.slice(0, -1), { ...last, content: streamingContentRef.current }]
              }
              return [...prev, {
                id: 'streaming',
                role: 'assistant',
                content: streamingContentRef.current,
                created_at: new Date().toISOString()
              }]
            })
            setStreaming(true)
          } else if (data.type === 'complete') {
            setStreaming(false)
            setActivity([])
            if (data.stats) setStats(data.stats)
            if (data.draft_html) setDraftHtml(data.draft_html)
            if (streamingContentRef.current) {
              const finalContent = streamingContentRef.current
              streamingContentRef.current = ''
              setMessages(prev => {
                const withoutStreaming = prev.filter(m => m.id !== 'streaming')
                return [...withoutStreaming, {
                  id: `msg-${Date.now()}`,
                  role: 'assistant',
                  content: finalContent,
                  created_at: new Date().toISOString()
                }]
              })
            }
          } else if (data.type === 'error') {
            setStreaming(false)
            if (data.draft_html) setDraftHtml(data.draft_html)
            const streamedContent = streamingContentRef.current
            streamingContentRef.current = ''
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              const msgs = streamedContent
                ? [...withoutStreaming, { id: `msg-${Date.now()}`, role: 'assistant', content: streamedContent, created_at: new Date().toISOString() }]
                : withoutStreaming
              return [...msgs, {
                id: `err-${Date.now()}`,
                role: 'assistant',
                content: `Error: ${data.message || 'Something went wrong'}`,
                created_at: new Date().toISOString()
              }]
            })
          } else if (data.type === 'init') {
            setAgentInfo({ model: data.model, tools: data.tools })
            setStreaming(true)
          } else if (data.type === 'activity' && data.summary) {
            setActivity(prev => [...prev, {
              id: `act-${Date.now()}-${Math.random()}`,
              tool: data.tool || 'Unknown',
              summary: data.summary!,
              detail: data.detail,
              turn: data.turn,
              timestamp: Date.now(),
            }].slice(-50))
            setStreaming(true)
          } else if (data.type === 'thinking') {
            setActivity(prev => [...prev, {
              id: `think-${Date.now()}`,
              tool: 'Thinking',
              summary: data.content || '',
              timestamp: Date.now(),
            }].slice(-50))
          } else if (data.type === 'tool_result') {
            // Mark last matching tool use as completed — we use this to show a ✓ in the activity log
            setActivity(prev => prev.map(a =>
              a.id === prev[prev.length - 1]?.id
                ? { ...a, detail: { ...a.detail, result_preview: data.preview, is_error: data.is_error } }
                : a
            ))
          } else if (data.type === 'draft_update' && data.draft_html) {
            setDraftHtml(data.draft_html)
          } else if (data.type === 'ask_user' && data.question) {
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              return [...withoutStreaming, {
                id: `ask-${Date.now()}`,
                role: 'assistant',
                content: data.question!.question,
                created_at: new Date().toISOString(),
                message_type: 'ask_user',
                metadata: data.question
              }]
            })
          } else if (data.type === 'recorded') {
            setRecording(false)
            setStatus('recorded')
            setRecordError(null)
            if (data.clip_url) setClipUrl(data.clip_url)
          } else if (data.type === 'record_error') {
            setRecording(false)
            setStatus('failed')
            setRecordError(data.message || 'Recording failed')
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [session.id])

  const handleSend = useCallback((content: string) => {
    const optimisticMsg: Message = {
      id: `opt-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, optimisticMsg])
    setStreaming(true)

    fetch(`/demos/${session.id}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ message: { content } })
    }).then(res => {
      if (res && !res.ok) setStreaming(false)
    }).catch(() => setStreaming(false))
  }, [session.id])

  const handleRecord = useCallback(() => {
    if (!draftHtml) return
    setRecording(true)
    setRecordError(null)
    setStatus('recording')
    fetch(`/demos/${session.id}/record`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      }
    }).catch(() => {
      setRecording(false)
      setStatus('failed')
      setRecordError('Request failed')
    })
  }, [session.id, draftHtml])

  const handleAnswer = useCallback((answer: string) => {
    setMessages(prev => prev.map(m =>
      m.message_type === 'ask_user' && !m.answered ? { ...m, answered: answer } : m
    ))
    fetch(`/demos/${session.id}/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ answer })
    })
  }, [session.id])

  const statusBadge = () => {
    const map: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'border-amber-200 bg-amber-50 text-amber-600' },
      recording: { label: 'Recording…', className: 'border-blue-200 bg-blue-50 text-blue-600' },
      recorded: { label: 'Recorded', className: 'border-emerald-200 bg-emerald-50 text-emerald-600' },
      failed: { label: 'Failed', className: 'border-red-200 bg-red-50 text-red-600' },
    }
    const s = map[status] || map.draft
    return (
      <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-medium ${s.className}`}>
        {s.label}
      </span>
    )
  }

  return (
    <Layout fullWidth>
      <div className="flex h-full flex-col">
        {/* Top Bar */}
        <div className="flex min-h-[52px] items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => router.visit('/demos')}
              className="p-1 text-gray-400 transition-colors hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-400">Demos</span>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-medium text-gray-900">{session.name}</span>
            {statusBadge()}
            {session.mode === 'template' && (
              <span className="inline-flex items-center rounded border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-600">
                Template
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {clipUrl && (
              <a
                href={clipUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Download MP4
              </a>
            )}
            {session.mode !== 'template' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm('Save this demo as a reusable template?')) {
                    fetch(`/demos/${session.id}/save_as_template`, {
                      method: 'POST',
                      headers: {
                        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
                      },
                    }).then(() => router.visit('/demos'))
                  }
                }}
                disabled={!draftHtml}
              >
                <Copy className="mr-1 h-3 w-3" />
                Save as Template
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm(`Delete "${session.name}"? This cannot be undone.`)) {
                  router.delete(`/demos/${session.id}`)
                }
              }}
              className="text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
            {session.mode !== 'template' && (
              <Button
                size="sm"
                onClick={handleRecord}
                disabled={recording || !draftHtml || streaming}
              >
                {recording ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Video className="mr-1 h-3 w-3" />
                )}
                {recording ? 'Recording…' : clipUrl ? 'Re-record' : 'Record'}
              </Button>
            )}
          </div>
        </div>

        {recordError && (
          <div className="border-b border-red-200 bg-red-50 px-5 py-2 text-xs text-red-600">
            Recording error: {recordError}
          </div>
        )}

        {/* Split Panel */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat */}
          <div className="flex w-[45%] min-w-[380px] flex-col border-r border-gray-200">
            {session.mode !== 'template' && (
              <DemoBriefPanel
                session={session}
                referenceCandidates={referenceCandidates || []}
                onRerun={handleSend}
                streaming={streaming}
                hasMessages={messages.length > 0}
              />
            )}
            <ChatPanel
              messages={messages}
              streaming={streaming}
              onAnswer={handleAnswer}
              activity={activity}
              agentInfo={agentInfo}
              stats={stats}
            />
            <ChatInput onSend={handleSend} disabled={streaming} />
          </div>

          {/* Preview */}
          <PreviewPanel html={draftHtml} clipUrl={clipUrl} status={status} />
        </div>
      </div>
    </Layout>
  )
}
